/**
 * Helpers for the `x-mia-acl-context` header sent with every authenticated request,
 * carrying the user's currently selected authorization organization/tenant.
 *
 * The value is a base64url-encoded (no padding) JSON string of shape
 * `{ organization, tenant, tenantName? }`. Mirrors ai-foundry-website/src/lib/aclContext.ts.
 *
 * NOTE: this authorization "organization/tenant" is a DIFFERENT concept from Flow's
 * existing Console company selector (see utils/tenantManager.js). It scopes the RBAC
 * context resolved by access-control/OPA, not the Console projects/marketplace view.
 *
 * Reads/writes go through localStorage directly so plain (non-hook) modules can build
 * the header without threading the selection through every call.
 */

export const ACL_CONTEXT_HEADER = 'x-mia-acl-context'

const STORAGE_KEY = 'app-selected-organization'

const isSelectedOrganization = (value) =>
  value !== null &&
  typeof value === 'object' &&
  typeof value.organization === 'string' &&
  typeof value.tenant === 'string'

export const readSelectedOrganization = () => {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === null) { return null }
  try {
    const parsed = JSON.parse(stored)
    return isSelectedOrganization(parsed) ? parsed : null
  } catch {
    return null
  }
}

export const writeSelectedOrganization = (selection) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(selection))
}

const base64UrlEncode = (value) => {
  const utf8Bytes = new TextEncoder().encode(value)
  const binaryString = Array.from(utf8Bytes, (byte) => String.fromCharCode(byte)).join('')
  return btoa(binaryString).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

export const encodeAclContext = (selection) => base64UrlEncode(JSON.stringify(selection))

/** Header for the currently selected org/tenant, or `{}` if nothing is selected yet. */
export const buildAclContextHeaders = () => {
  const selection = readSelectedOrganization()
  return selection ? { [ACL_CONTEXT_HEADER]: encodeAclContext(selection) } : {}
}

/** Header for a specific org/tenant (used to probe per-organization product access). */
export const buildOrganizationAclContextHeaders = (organization, tenant) => (
  { [ACL_CONTEXT_HEADER]: encodeAclContext({ organization, tenant }) }
)
